import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatFCFA } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, CheckCircle2, RefreshCcw, AlertCircle, Phone, Plus, LogOut } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

export default function Sejours() {
  const [tab, setTab] = useState("active");
  const [data, setData] = useState({ active: [], overdue: [], completed: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [extendRow, setExtendRow] = useState(null);
  const [extendForm, setExtendForm] = useState({ days: 7, montant: 0, methode: "Espèces", reference: "" });
  const [extending, setExtending] = useState(false);
  const [closeRow, setCloseRow] = useState(null);
  const [closeForm, setCloseForm] = useState({ actual_end_date: "", notes: "" });
  const [closing, setClosing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/sejours");
      setData(res.data || { active: [], overdue: [], completed: [] });
    } catch {
      toast.error("Impossible de charger les séjours.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openExtend = (row) => {
    setExtendRow(row);
    // Suggest same duration and same amount as previous stay
    const dailyRate = row.sejour_days > 0 ? Math.round(row.montant / row.sejour_days) : 0;
    setExtendForm({
      days: row.sejour_days || 7,
      montant: dailyRate * (row.sejour_days || 7) || row.montant,
      methode: row.methode || "Espèces",
      reference: "",
    });
  };

  const submitExtend = async () => {
    if (!extendRow) return;
    const days = Number(extendForm.days || 0);
    const montant = Number(extendForm.montant || 0);
    if (days <= 0 || montant <= 0) {
      toast.error("Renseignez une durée et un montant valides.");
      return;
    }
    setExtending(true);
    try {
      // The new stay starts the day AFTER the current end date.
      const newStart = new Date(extendRow.sejour_end_date + "T00:00:00");
      newStart.setDate(newStart.getDate() + 1);
      const startIso = newStart.toISOString().slice(0, 10);
      await api.post("/paiements", {
        locataire_id: extendRow.locataire_id,
        maison_id: extendRow.maison_id,
        montant,
        mois: startIso.slice(0, 7),
        date_paiement: new Date().toISOString(),
        methode: extendForm.methode,
        reference: extendForm.reference || "",
        notes: `Prolongation du séjour du ${extendRow.sejour_end_date}`,
        sejour_days: days,
        sejour_start_date: startIso,
      });
      const newEnd = new Date(newStart.getTime() + days * 86400000);
      toast.success(`Séjour prolongé jusqu'au ${newEnd.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`);
      setExtendRow(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setExtending(false);
    }
  };

  const filterList = (list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      (r.locataire_nom || "").toLowerCase().includes(q) ||
      (r.maison_nom || "").toLowerCase().includes(q) ||
      (r.locataire_telephone || "").toLowerCase().includes(q));
  };

  const activeRows = filterList(data.active);
  const overdueRows = filterList(data.overdue || []);
  const doneRows = filterList(data.completed);

  const openClose = (row) => {
    setCloseRow(row);
    setCloseForm({ actual_end_date: new Date().toISOString().slice(0, 10), notes: "" });
  };

  const submitClose = async () => {
    if (!closeRow) return;
    setClosing(true);
    try {
      await api.post(`/paiements/${closeRow.paiement_id}/close-sejour`, {
        actual_end_date: closeForm.actual_end_date,
        notes: closeForm.notes || null,
      });
      toast.success(`Séjour clôturé au ${fmtDate(closeForm.actual_end_date)}`);
      setCloseRow(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setClosing(false);
    }
  };

  const remainingBadge = (n) => {
    if (n < 0) return <Badge className="bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-100">Dépassé</Badge>;
    if (n === 0) return <Badge className="bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100">Expire aujourd'hui</Badge>;
    if (n === 1) return <Badge className="bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-100">Expire demain</Badge>;
    if (n <= 7) return <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">{n} j restants</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">{n} j restants</Badge>;
  };

  return (
    <div>
      <PageHeader
        title="Séjours"
        subtitle="Suivi des séjours en cours et terminés pour les locataires temporaires."
        testid="page-sejours"
        accent="orange"
        actions={
          <Button variant="outline" onClick={load} data-testid="btn-refresh-sejours">
            <RefreshCcw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 rounded-xl border border-orange-200 shadow-sm bg-gradient-to-br from-orange-50 to-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-orange-700">En cours</div>
          <div className="font-heading text-3xl font-bold text-orange-900 mt-1" data-testid="stat-active">{data.active.length}</div>
        </Card>
        <Card className="p-5 rounded-xl border border-rose-200 shadow-sm bg-gradient-to-br from-rose-50 to-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700">Dépassés — action requise</div>
          <div className="font-heading text-3xl font-bold text-rose-900 mt-1" data-testid="stat-overdue">{(data.overdue || []).length}</div>
        </Card>
        <Card className="p-5 rounded-xl border border-slate-200 shadow-sm bg-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Terminés</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-1" data-testid="stat-completed">{data.completed.length}</div>
        </Card>
        <Card className="p-5 rounded-xl border border-emerald-200 shadow-sm bg-white">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700">Revenus (en cours)</div>
          <div className="font-heading text-2xl font-bold text-emerald-800 mt-1" data-testid="stat-revenue">
            {formatFCFA(data.active.reduce((s, r) => s + (r.montant || 0), 0))}
          </div>
        </Card>
      </div>

      <Card className="p-4 rounded-xl border border-slate-200 shadow-sm bg-white mb-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="w-full md:w-80">
            <Label className="text-xs">Rechercher</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Locataire, téléphone ou maison…"
              data-testid="input-sejour-search"
            />
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100" data-testid="sejours-tabs">
          <TabsTrigger value="active" data-testid="tab-active">
            <CalendarClock className="w-4 h-4 mr-1.5" /> En cours ({activeRows.length})
          </TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue" className="data-[state=active]:text-rose-700">
            <AlertCircle className="w-4 h-4 mr-1.5" /> Dépassés ({overdueRows.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> Terminés ({doneRows.length})
          </TabsTrigger>
        </TabsList>

        {[
          { key: "active", rows: activeRows, empty: "Aucun séjour en cours.", showRemaining: true, showActions: true, overdueMode: false },
          { key: "overdue", rows: overdueRows, empty: "Aucun séjour dépassé — tout est à jour !", showRemaining: true, showActions: true, overdueMode: true },
          { key: "completed", rows: doneRows, empty: "Aucun séjour terminé.", showRemaining: false, showActions: false, overdueMode: false },
        ].map(({ key, rows, empty, showRemaining, showActions, overdueMode }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Locataire</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Début</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Fin</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">Durée</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-emerald-700 font-semibold text-right">Montant</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      {showRemaining ? "Statut" : "Fin de séjour"}
                    </TableHead>
                    {showActions && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={showActions ? 8 : 7} className="text-center text-slate-500 py-12">Chargement…</TableCell></TableRow>
                  )}
                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={showActions ? 8 : 7} className="text-center text-slate-500 py-14">
                        <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                        {empty}
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && rows.map((r) => (
                    <TableRow key={r.paiement_id} className="hover:bg-slate-50/60" data-testid={`sejour-row-${r.paiement_id}`}>
                      <TableCell className="font-medium text-slate-900">
                        <div>{r.locataire_nom}</div>
                        {r.locataire_telephone && (
                          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" /> {r.locataire_telephone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-700">{r.maison_nom}</TableCell>
                      <TableCell className="text-slate-600">{fmtDate(r.sejour_start_date)}</TableCell>
                      <TableCell className="text-slate-600">{fmtDate(r.sejour_end_date)}</TableCell>
                      <TableCell className="text-center text-slate-700 font-medium">
                        {r.sejour_days ? `${r.sejour_days} j` : "—"}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">{formatFCFA(r.montant)}</TableCell>
                      <TableCell>
                        {showRemaining ? remainingBadge(r.days_remaining) : (
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">
                            {r.sejour_status === "cloture" ? "Clôturé le " : "Terminé le "}
                            {fmtDate(r.sejour_actual_end_date || r.sejour_end_date)}
                          </Badge>
                        )}
                      </TableCell>
                      {showActions && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openClose(r)}
                              className="border-slate-300 text-slate-700 hover:bg-slate-100"
                              data-testid={`btn-close-${r.paiement_id}`}
                              title="Clôturer le séjour (locataire parti)"
                            >
                              <LogOut className="w-4 h-4 mr-1" /> Clôturer
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openExtend(r)}
                              className={overdueMode ? "bg-rose-500 hover:bg-rose-600 text-white" : "bg-orange-500 hover:bg-orange-600 text-white"}
                              data-testid={`btn-extend-${r.paiement_id}`}
                            >
                              <Plus className="w-4 h-4 mr-1" /> Prolonger
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!closeRow} onOpenChange={(o) => !o && setCloseRow(null)}>
        <DialogContent data-testid="dialog-close-sejour">
          <DialogHeader>
            <DialogTitle className="font-heading">Clôturer le séjour</DialogTitle>
          </DialogHeader>
          {closeRow && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
                <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">Séjour concerné</div>
                <div className="text-slate-900 font-medium">{closeRow.locataire_nom} — {closeRow.maison_nom}</div>
                <div className="text-slate-600 mt-1">
                  Fin planifiée : <span className="font-semibold text-slate-900">{fmtDate(closeRow.sejour_end_date)}</span>
                </div>
              </div>
              <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3">
                <div className="space-y-2">
                  <Label className="text-xs">Date de départ réelle</Label>
                  <Input
                    type="date"
                    value={closeForm.actual_end_date}
                    onChange={(e) => setCloseForm({ ...closeForm, actual_end_date: e.target.value })}
                    data-testid="input-close-date"
                  />
                  <p className="text-[11px] text-slate-500">
                    La date à laquelle le locataire a réellement quitté. Peut être identique, antérieure ou postérieure à la date planifiée.
                  </p>
                </div>
                <div className="space-y-2 mt-3">
                  <Label className="text-xs">Note (facultatif)</Label>
                  <Input
                    value={closeForm.notes}
                    onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })}
                    placeholder="Ex: parti plus tôt à sa demande, non-retour…"
                    data-testid="input-close-notes"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Après clôture, ce séjour bascule dans l'onglet <strong>Terminés</strong> et n'apparaîtra plus dans les notifications.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseRow(null)}>Annuler</Button>
            <Button
              onClick={submitClose}
              disabled={closing || !closeForm.actual_end_date}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="btn-confirm-close"
            >
              {closing ? "Enregistrement…" : "Confirmer la clôture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extendRow} onOpenChange={(o) => !o && setExtendRow(null)}>
        <DialogContent data-testid="dialog-extend-sejour">
          <DialogHeader>
            <DialogTitle className="font-heading">Prolonger le séjour</DialogTitle>
          </DialogHeader>
          {extendRow && (() => {
            const currEnd = new Date(extendRow.sejour_end_date + "T00:00:00");
            const newStart = new Date(currEnd.getTime() + 86400000);
            const days = Number(extendForm.days || 0);
            const newEnd = days > 0 ? new Date(newStart.getTime() + days * 86400000) : null;
            return (
              <div className="space-y-4">
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
                  <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">Séjour actuel</div>
                  <div className="text-slate-900 font-medium">{extendRow.locataire_nom} — {extendRow.maison_nom}</div>
                  <div className="text-slate-600 mt-1">
                    Fin actuelle : <span className="font-semibold text-slate-900">{fmtDate(extendRow.sejour_end_date)}</span>
                  </div>
                </div>
                <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.16em] font-bold text-orange-800 mb-2">Nouvelle période</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Jours supplémentaires</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={extendForm.days}
                        onChange={(e) => setExtendForm({ ...extendForm, days: Number(e.target.value || 0) })}
                        data-testid="input-extend-days"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Montant (FCFA)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={extendForm.montant}
                        onChange={(e) => setExtendForm({ ...extendForm, montant: Number(e.target.value || 0) })}
                        data-testid="input-extend-montant"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {[1, 3, 7, 14, 30].map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const dailyRate = extendRow.sejour_days > 0 ? extendRow.montant / extendRow.sejour_days : 0;
                          setExtendForm({ ...extendForm, days: d, montant: Math.round(dailyRate * d) || extendForm.montant });
                        }}
                        className={`px-2.5 py-1 rounded text-xs font-medium border transition ${
                          extendForm.days === d
                            ? "bg-orange-500 border-orange-600 text-white"
                            : "bg-white border-orange-300 text-orange-800 hover:bg-orange-100"
                        }`}
                      >
                        {d}j
                      </button>
                    ))}
                  </div>
                  <div className="text-sm text-slate-700 mt-3">
                    Début : <span className="font-medium">{fmtDate(newStart.toISOString().slice(0, 10))}</span>
                    {newEnd && <><br />Fin : <span className="font-semibold text-orange-900" data-testid="extend-new-end">{fmtDate(newEnd.toISOString().slice(0, 10))}</span></>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Méthode</Label>
                    <Select value={extendForm.methode} onValueChange={(v) => setExtendForm({ ...extendForm, methode: v })}>
                      <SelectTrigger data-testid="select-extend-methode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Espèces">Espèces</SelectItem>
                        <SelectItem value="Virement">Virement</SelectItem>
                        <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                        <SelectItem value="Chèque">Chèque</SelectItem>
                        <SelectItem value="Avance">Avance</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Référence (facultatif)</Label>
                    <Input
                      value={extendForm.reference}
                      onChange={(e) => setExtendForm({ ...extendForm, reference: e.target.value })}
                      placeholder="N° transaction, reçu…"
                      data-testid="input-extend-reference"
                    />
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendRow(null)}>Annuler</Button>
            <Button
              onClick={submitExtend}
              disabled={extending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-confirm-extend"
            >
              {extending ? "Enregistrement…" : "Enregistrer la prolongation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
